<template>
  <NuxtLink
    class="ice-breaker-card"
    :class="[props.color, props.pattern]"
    :to="
      localePath({
        name: 'ice-breaker-games-slug',
        params: { slug: props.slug },
      })
    "
  >
    <UiComponentParagraph class="duration">
      {{ props.duration }} min
    </UiComponentParagraph>
    <UiComponentWrapper class="metadata">
      <UiComponentParagraph class="title">
        {{ props.title }}
      </UiComponentParagraph>
      <UiComponentParagraph class="description">
        {{ props.text }}
      </UiComponentParagraph>
    </UiComponentWrapper>
  </NuxtLink>
</template>

<script setup>
// Composables
const localePath = useLocalePath()

// Props
const props = defineProps({
  color: {
    required: true,
    type: String,
    validator(value) {
      return ['blizzard', 'live-red', 'sirocco', 'winter-green'].indexOf(value) !== -1
    },
  },
  duration: {
    required: true,
    type: String,
  },
  pattern: {
    required: true,
    type: String,
    validator(value) {
      return ['brick', 'circle', 'concentric', 'cross', 'double-bubble', 'hexagon-1', 'hexagon-2', 'lines', 'scales', 'sprinkles'].indexOf(value) !== -1
    },
  },
  title: {
    required: true,
    type: String,
  },
  text: {
    required: true,
    type: String,
  },
  slug: {
    required: true,
    type: String,
  },
})
</script>

<style lang="postcss" scoped>
.ice-breaker-card {
  @apply relative flex flex-col justify-between h-80 w-full p-[26px] border-[6px] border-white rounded-2xl shadow-light1 transition-shadow duration-600 ease-in-out;

  &:hover {
    @apply shadow-light6;

    &::after {
      @apply opacity-[0.16];
    }
  }

  &::before {
    @apply absolute inset-0 bg-repeat opacity-5 content-[''] z-0;
  }

  &::after {
    @apply absolute inset-0 opacity-0 content-[''] z-10 transition-opacity duration-600 ease-in-out;
    background: linear-gradient(141deg, rgba(255, 255, 255, 0.00) 28.5%, #FFF 48.57%, rgba(255, 255, 255, 0.00) 69%);
  }

  &.blizzard {
    @apply bg-blizzard-600;
  }

  &.live-red {
    @apply bg-live-red-600;
  }

  &.sirocco {
    @apply bg-sirocco-600;
  }

  &.winter-green {
    @apply bg-winter-green-500;
  }

  &.brick {
    &::before {
      @apply bg-[url('~/assets/pattern/brick.svg')] bg-[length:61px_60px];
    }
  }

  &.circle {
    &::before {
      @apply bg-[url('~/assets/pattern/circle.svg')] bg-[length:60px_60px];
    }
  }

  &.concentric {
    &::before {
      @apply bg-[url('~/assets/pattern/concentric.svg')] bg-[length:61px_60px];
    }
  }

  &.cross {
    &::before {
      @apply bg-[url('~/assets/pattern/cross.svg')] bg-[length:61px_60px];
    }
  }

  &.double-bubble {
    &::before {
      @apply bg-[url('~/assets/pattern/double-bubble.svg')] bg-[length:61px_60px];
    }
  }

  &.hexagon-1 {
    &::before {
      @apply bg-[url('~/assets/pattern/hexagon-1.svg')] bg-[length:41px_60px];
    }
  }

  &.hexagon-2 {
    &::before {
      @apply bg-[url('~/assets/pattern/hexagon-2.svg')] bg-[length:36px_60px];
    }
  }

  &.lines {
    &::before {
      @apply bg-[url('~/assets/pattern/lines.svg')] bg-[length:61px_60px];
    }
  }

  &.scales {
    &::before {
      @apply bg-[url('~/assets/pattern/scales.svg')] bg-[length:61px_60px];
    }
  }

  &.sprinkles {
    &::before {
      @apply bg-[url('~/assets/pattern/sprinkles.svg')] bg-[length:61px_60px];
    }
  }

  .duration {
    @apply self-end font-medium text-sm text-white z-20;
  }

  .metadata {
    @apply flex-col gap-1 z-20;

    p {
      @apply text-white;

      &.title {
        @apply font-medium text-2xl leading-[normal] line-clamp-3;
      }

      &.description {
        @apply text-sm leading-6 line-clamp-4;
      }
    }
  }
}
</style>
